import {
  Button,
  CloseButton,
  CondensedButton,
  NumberInput,
  Popover,
  Spinner,
  Text,
  View,
} from "@instructure/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfigService } from "../../../services/ConfigService";
import { SubjectService } from "../../../services/SubjectService";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";

/**
 * ResultsDeadlinePopover component has 2 use cases
 * 1. Department specific deadline (accepts department & setDepartment props)
 * 2. Default deadline (accepts defaultDeadline & setDefaultDeadline props)
 */
const ResultsDeadlinePopover = ({
  department,
  setDepartment,
  defaultDeadline,
  setDefaultDeadline,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();

  function getInitialDeadline() {
    const deadline =
      department?.days_after_term_finalization_deadline ?? defaultDeadline;
    return deadline >= 0 ? deadline : "";
  }
  const [deadlineInputValue, setDeadlineInputValue] =
    useState(getInitialDeadline);
  const [deadlineText, setDeadlineText] = useState(getInitialDeadline());
  const [openPopover, setOpenPopover] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  function handleChangeDeadline(_e, value) {
    let inputValue = value.replace(/\D/g, "");
    if (inputValue) {
      inputValue = Number(inputValue);
      if (inputValue !== deadlineInputValue) {
        inputValue = Math.round(value);
        if (inputValue > 30) {
          setDeadlineInputValue(30);
        } else {
          setDeadlineInputValue(inputValue);
        }
      }
    } else {
      setDeadlineInputValue("");
    }
  }

  function handleIncrementDeadline() {
    setDeadlineInputValue((prev) => (prev === 30 ? prev : Number(prev + 1)));
  }

  function handleDecrementDeadline() {
    setDeadlineInputValue((prev) => (prev > 0 ? Number(prev - 1) : prev));
  }

  async function handleUpdateDepartmentDeadline(deadline) {
    try {
      setSubmitting(true);
      const deadlinePayload = deadline === "" ? -1 : deadline;
      await SubjectService.upsertDepartment(department, deadlinePayload);
      setDepartment((prev) => ({
        ...prev,
        days_after_term_finalization_deadline: deadline,
      }));
      setDeadlineText(deadline);
      showNotification(
        t("notifications.general.updateSuccess", {
          obj: t("resultsDeadline.deadlineForDept", { dept: department.name }),
        })
      );
      setOpenPopover(false);
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("resultsDeadline.deadlineForDept", { dept: department.name }),
        }),
        ERROR
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdateDefaultDeadline(deadline) {
    try {
      setSubmitting(true);
      const deadlinePayload = deadline === "" ? -1 : deadline;
      await ConfigService.setGlobalDaysAfterTermDeadline(deadlinePayload);
      setDefaultDeadline(deadline);
      setDeadlineText(deadline);
      showNotification(
        t("notifications.general.updateSuccess", {
          obj: t("resultsDeadline.defaultDeadline"),
        })
      );
      setOpenPopover(false);
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("resultsDeadline.defaultDeadline"),
        }),
        ERROR
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleClosePopover() {
    setOpenPopover(false);
    setDeadlineInputValue(getInitialDeadline());
  }

  return (
    <Popover
      renderTrigger={
        <CondensedButton
          data-testid={
            department
              ? `results-deadline-button-${department.name}`
              : "results-deadline-button"
          }
          margin="0 0 xxx-small 0"
        >
          {deadlineText || deadlineText === 0 ? (
            t("resultsDeadline.numDaysAfterTerm", { num: deadlineText })
          ) : (
            <Text fontStyle="italic">
              {t("resultsDeadline.noneSetDeadline")}
            </Text>
          )}
        </CondensedButton>
      }
      isShowingContent={openPopover}
      onShowContent={() => setOpenPopover(true)}
      onHideContent={handleClosePopover}
      on="click"
      screenReaderLabel={
        department
          ? t("resultsDeadline.setDeadlineForDeptLabel", {
              dept: department.name,
            })
          : t("resultsDeadline.setDefaultDeadlineLabel")
      }
      shouldContainFocus
    >
      <View as="div" padding="medium" display="block">
        <CloseButton
          placement="end"
          offset="small"
          onClick={handleClosePopover}
          screenReaderLabel={t("global.close")}
        />
        <NumberInput
          id="days-after-term-end-input"
          renderLabel={t("resultsDeadline.daysAfterTerm")}
          placeholder="(0-30)"
          value={deadlineInputValue}
          onChange={handleChangeDeadline}
          onIncrement={handleIncrementDeadline}
          onDecrement={handleDecrementDeadline}
          allowStringValue
        />
        <Button
          margin="small 0 0 0"
          color="primary"
          display="block"
          onClick={() => {
            if (department) {
              handleUpdateDepartmentDeadline(deadlineInputValue);
            } else {
              handleUpdateDefaultDeadline(deadlineInputValue);
            }
          }}
          disabled={submitting}
        >
          {submitting ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            t("global.submit")
          )}
        </Button>
      </View>
    </Popover>
  );
};

export default ResultsDeadlinePopover;
